import { MsUserApi } from "@microtica/ms-usermanagement-sdk";
import { MsProjectApi } from "@microtica/ms-project-sdk";
import { MsKubeApi } from "@microtica/ms-kube-sdk";
import { MsElasticsearchApi } from "@microtica/ms-elasticsearch-sdk";
import { MsEngineApi } from "@microtica/ms-engine-sdk";
import { MsAwsApi } from "@microtica/ms-aws-sdk";
import { MsApApi } from '@microtica/ms-ap-sdk';
import { MsAiApi } from "@microtica/ms-ai-sdk";
import { MsNotificationApi } from "@microtica/ms-notification-sdk";
import { MsMonitoringApi, Configuration } from "@microtica/ms-monitoring-sdk";
import { env } from "../env";
import { Socket } from "socket.io-client";
import { AxiosInstance } from "axios";
import axios from "axios";
import { registerRequestInterceptor } from "./interceptors";
import { CostOptimizerApi } from "./cost-optimizer";

const axiosInstance = axios.create() as any;
registerRequestInterceptor(axiosInstance);

export function getBaseUrl(): string {
    return env.REACT_APP_BACKEND_ENDPOINT;
};

export function getIdToken(): string | undefined {
    if (process.env.NODE_ENV === "test") {
        return "testing_access_token";
    }
    return localStorage.getItem("idToken") || undefined;
}

export function getUserManagementService() {
    return new MsUserApi(
        {
            accessToken: getIdToken()
        },
        `${getBaseUrl()}/ms-usermanagement`,
        axiosInstance
    );
}

export function getProjectService() {
    return new MsProjectApi(
        {
            accessToken: getIdToken()
        },
        `${getBaseUrl()}/ms-project`,
        axiosInstance
    );
}

export function getKubeService() {
    return new MsKubeApi(
        {
            accessToken: getIdToken()
        },
        `${getBaseUrl()}/ms-kube`,
        axiosInstance
    );
}

export function getElasticSearchService() {
    return new MsElasticsearchApi(
        {
            accessToken: getIdToken()
        },
        `${getBaseUrl()}/ms-elasticsearch`,
        axiosInstance
    );
}

export function getEnvironmentService() {
    return new MsEngineApi(
        {
            accessToken: getIdToken()
        },
        `${getBaseUrl()}/ms-engine`,
        axiosInstance
    );
}

export function getCloudService() {
    return new MsAwsApi(
        {
            accessToken: getIdToken()
        },
        `${getBaseUrl()}/ms-aws`,
        axiosInstance
    );
}

export function getPipelinesService() {
    return new MsApApi(
        {
            accessToken: getIdToken()
        },
        `${getBaseUrl()}/ms-ap`,
        axiosInstance
    );
}

export function getAIService() {
    const axiosInstance: AxiosInstance = axios.create({
        baseURL: `${getBaseUrl()}/ms-ai`
    })
    const configuration: Configuration = new Configuration({
        accessToken: getIdToken(),
    })
    return new MsAiApi(
        configuration,
        `${getBaseUrl()}/ms-ai`,
        axiosInstance
    );
}

/**
 * Feature-flag gate for the API Keys page. Comma-separated allow-list
 * of project ids in `REACT_APP_API_KEYS_PROJECTS`, or `*` to enable
 * for everyone. Empty / missing => off.
 */
export function isApiKeysEnabled(projectId: string): boolean {
    const list = (env.REACT_APP_API_KEYS_PROJECTS || "")
        .split(",")
        .map(s => s.trim())
        .filter(Boolean);
    return list.includes("*") || list.includes(projectId);
}

export function isAIServiceEnabled(projectId: string) {
    const eligibleProjectIds = [
        "kyvqkytu4wfax5rej2by",
        "3n8uawcd2v3q92uregxz",
        "8rx83ionr4sue3ptepen",
        "8v8np31a6k8v8fm72qds",
        "d6rv7d69wawv5mrpdswk",
        "kmq3v6sjq8wewk697rb1",
        "og3oc991bmud4n7badoj",
        "pnyjxursbt1sh9j2faog",
        "pt4ojxn3ge1jdyhcd8yk",
        "zxyckxij2kqt1gan5dnm"
    ];
    return env.REACT_APP_BACKEND_ENDPOINT.includes("dev") ?
        true :
        eligibleProjectIds.includes(projectId)
}

export function getMonitoringService() {
    const axiosInstance: AxiosInstance = axios.create({
        baseURL: `${getBaseUrl()}/ms-monitoring`
    })
    const configuration: Configuration = new Configuration({
        accessToken: getIdToken(),
    })
    return new MsMonitoringApi(
        configuration,
        `${getBaseUrl()}/ms-monitoring`,
        axiosInstance
    );
}

export function getNotificationService() {
    return new MsNotificationApi(
        {
            accessToken: getIdToken()
        },
        `${getBaseUrl()}/ms-notification`,
        axiosInstance
    );
}

export function getCostOptimizationService() {
    return new CostOptimizerApi(
        `${getBaseUrl()}/ms-cloud-cost-optimizer`,
        axiosInstance,
        getIdToken
    );
}

export function getRealtimeService(queryParams: any) {
    return new RealtimePollingSocket(
        getBaseUrl(),
        queryParams,
        getIdToken()
    ) as unknown as Socket;
}

export function disconnectRealtimeService() {
    // Kept for call-site compatibility. Each returned polling client owns its
    // AbortController and is stopped through socket.disconnect()/close().
    return undefined;
}

type RealtimeListener = (...args: any[]) => void;

class RealtimePollingSocket {
    private readonly listeners: { [event: string]: RealtimeListener[] } = {};
    private readonly pendingLogs: any[] = [];
    private readonly controller = new AbortController();
    private disconnected = false;
    private disconnectPending = false;

    constructor(
        private readonly baseUrl: string,
        private readonly queryParams: Record<string, unknown>,
        private readonly token?: string
    ) {
        void this.poll();
    }

    on(event: string, listener: RealtimeListener) {
        this.listeners[event] = [...(this.listeners[event] || []), listener];
        if (event === "logs" && this.pendingLogs.length) {
            for (const payload of this.pendingLogs.splice(0)) listener(payload);
        }
        if (event === "disconnect" && this.disconnectPending) {
            this.disconnectPending = false;
            listener("server namespace disconnect");
        }
        return this;
    }

    off(event: string, listener?: RealtimeListener) {
        if (!listener) this.listeners[event] = [];
        else this.listeners[event] = (this.listeners[event] || []).filter(item => item !== listener);
        return this;
    }

    disconnect() {
        this.finish();
        return this;
    }

    close() {
        return this.disconnect();
    }

    private emit(event: string, payload?: any) {
        const listeners = this.listeners[event] || [];
        if (listeners.length === 0) {
            if (event === "logs") this.pendingLogs.push(payload);
            if (event === "disconnect") this.disconnectPending = true;
            return;
        }
        for (const listener of listeners) listener(payload);
    }

    private finish() {
        if (this.disconnected) return;
        this.disconnected = true;
        this.controller.abort();
        this.emit("disconnect", "server namespace disconnect");
    }

    private async poll() {
        const type = String(this.queryParams.typeOfLogs || "");
        let cursor: string | undefined;

        try {
            while (!this.disconnected) {
                const url = new URL(`${this.baseUrl.replace(/\/$/, "")}/ms-realtime/logs`);
                url.searchParams.set("type", type);
                if (cursor) url.searchParams.set("cursor", cursor);
                for (const [key, value] of Object.entries(this.queryParams)) {
                    if (key !== "typeOfLogs" && value !== undefined) {
                        url.searchParams.set(key, String(value));
                    }
                }

                const response = await fetch(url.toString(), {
                    cache: "no-store",
                    credentials: "include",
                    headers: { Authorization: `Bearer ${this.token}` },
                    signal: this.controller.signal
                });
                if (!response.ok) throw new Error(`logs poll failed: ${response.status}`);

                const payload = await response.json() as {
                    events?: any[];
                    nextCursor?: string | null;
                    done?: boolean;
                };
                const events = Array.isArray(payload.events) ? payload.events : [];

                if (type === "pipeline") {
                    // Preserve the Socket.IO contract: pipeline listeners
                    // receive one string chunk, not one render/update per
                    // CloudWatch event or backend page.
                    const chunk = events.filter(event => typeof event === "string").join("");
                    if (chunk) this.emit("logs", chunk);
                } else if (type === "terraform") {
                    if (events.length) this.emit("logs", events);
                } else if (type === "service") {
                    this.emit("logs", { logs: events });
                }

                if (payload.done) break;
                cursor = payload.nextCursor || undefined;
                await new Promise(resolve => setTimeout(resolve, 3000));
            }
        } catch (err) {
            if (!this.controller.signal.aborted) console.error("Realtime logs polling failed", err);
        } finally {
            this.finish();
        }
    }
}
